import React, { useEffect, useRef, useState } from 'react'
import { addStoreLocaleByCookie } from 'lib/storeLocale/client'
import { deleteCollection } from 'apis/private/collection'
import useCollectionDuplicate from 'components/Artworks/Actions/Collections/duplicateHook'
import { ActionsLinks, ActionLink } from 'styles/algolia/collection/header'
import AuthenticationConnect, {
  ConnectorProps as AuthenticationProps,
} from 'connectors/Authentication'
import ActionsConnector, { ConnectorProps as ActionsProps } from '../../connectors/Actions'
import { editCollection, exportEmail, iosVIR, reorderCollection } from '../../helpers/actions'
import CreateCollectionsModal, {
  CreateCollectionsModalApi,
  getCreatedCollectionUrl,
} from '../../layouts/Modal'
type Props = ActionsProps & AuthenticationProps

const Component = (props: Props): React.ReactElement<React.ComponentProps<any>, any> => {
  const {
    actionLinks,
    addMessage,
    hasValidPermissions,
    idUserCollection,
    isAdmin,
    isAuthenticated,
    showModal,
    openModal,
    userId,
  } = props
  const DEFAULT_MESSAGE = {
    shouldShowMessage: false,
    actionStatus: false,
  }
  const { editUrl, collectionsUrl, exportEmailsUrl, iosVirUrl, reorderUrl } = actionLinks
  const [
    { shouldShowMessage: displayFlashMessage, actionStatus: successStatus },
    handleShowMessage,
  ] = useState(DEFAULT_MESSAGE)
  const [createCollectionAfterLogin, handleCreateCollectionAfterLogin] = useState(false)
  // If a user initiates a login for Duplicate and abandons to start a Create login, pass
  // the switch into the hook to prevent the Duplicate hook from completing.
  const [switchToCreateLogin, handleSwitchToCreateLogin] = useState(false)
  const onSuccess = handleShowMessage

  const createCollectionsModalRef = useRef<CreateCollectionsModalApi>(null)

  const handleOpenCreateCollectionsModal = () => {
    createCollectionsModalRef.current?.handleOpenModal()
  }

  const handleCreateCollectionOnSuccess = (
    payload: Record<string, any>,
    formData: Record<string, any>
  ) => {
    const newLocation = getCreatedCollectionUrl({ payload, formData, userId })

    window.location.assign(newLocation)
  }

  /*
    The useCollectionDuplicate hook handles login validation and then
    continues the lifecycle of the Duplicate action, returning success
    status in the callback for flash messaging.
   */
  const { onClickCollectionDuplicate, newCollectionId } = useCollectionDuplicate({
    idUserCollection,
    isAuthenticated,
    onSuccess,
    showModal,
    openModal,
    switchToCreateLogin,
  })
  useEffect(() => {
    if (displayFlashMessage) {
      // Flash a status message once the hook action is complete
      const message = successStatus
        ? 'Successfully duplicated collection'
        : 'Problem duplicating collection'
      addMessage(message, 'success')
    }
  }, [displayFlashMessage])
  useEffect(() => {
    // Once we have a successful duplication, redirect to the edit page
    if (successStatus && newCollectionId > 0) {
      const duplicateUrl = `/collections/edit/owner/${userId}/collection/${newCollectionId}`
      const location = addStoreLocaleByCookie(duplicateUrl)
      window.location = location
    }
  }, [newCollectionId, successStatus])

  const handleDeleteCollection = async () => {
    const confirmDeletion = confirm('Are you sure you want to delete this collection?')

    if (confirmDeletion) {
      const apiResponse = await deleteCollection(idUserCollection)
      const { success } = apiResponse

      if (!success) {
        addMessage('Problem deleting collection', 'error')
      } else {
        addMessage('Successfully deleted collection', `success`)
        window.location.href = collectionsUrl
      }
    }
  }

  /*
    Ensure that user doesn't redirect to Create URL until
    successfully logged in. If login fails, they don't leave this page.
   */
  const handleCreateCollection = (): void => {
    if (!isAuthenticated) {
      openModal('login')
      handleCreateCollectionAfterLogin(true)
      return
    }

    handleOpenCreateCollectionsModal()
  }

  useEffect(() => {
    if (createCollectionAfterLogin) {
      // Make a call to update the EVENT
      handleCreateCollectionAfterLogin(false)
      handleOpenCreateCollectionsModal()
    }
  }, [isAuthenticated])

  // Admin OR Owner of Collection can view and edit collection
  return (
    <ActionsLinks data-type='aa-actionlinks'>
      {hasValidPermissions && (
        <>
          <ActionLink
            href={editUrl}
            title='Edit Collection Details'
            onClick={(ev) => {
              ev.preventDefault()
              editCollection(editUrl)
            }}
          >
            Edit
          </ActionLink>

          <ActionLink
            href={reorderUrl}
            title='Reorder Images in Collection'
            onClick={(ev) => {
              ev.preventDefault()
              reorderCollection(reorderUrl)
            }}
          >
            Reorder
          </ActionLink>
        </>
      )}

      <ActionLink
        href={collectionsUrl}
        title='Create a new collection'
        onClick={(ev) => {
          ev.preventDefault()
          handleSwitchToCreateLogin(true)
          handleCreateCollection()
        }}
      >
        Create a Collection
      </ActionLink>

      <ActionLink
        href={collectionsUrl}
        title='Create your own collection from this one'
        onClick={(ev) => {
          ev.preventDefault()
          handleCreateCollectionAfterLogin(false)
          handleSwitchToCreateLogin(false)
          onClickCollectionDuplicate()
        }}
      >
        Duplicate
      </ActionLink>

      {/* These should only be visible for admins */}
      {isAdmin && (
        <>
          <ActionLink
            href={exportEmailsUrl}
            title='Export Artist Emails from Collection'
            onClick={(ev) => {
              ev.preventDefault()
              exportEmail(exportEmailsUrl)
            }}
          >
            Export Emails
          </ActionLink>

          <ActionLink
            href={iosVirUrl}
            title='Click to Copy'
            onClick={(ev) => {
              ev.preventDefault()
              iosVIR(iosVirUrl)
            }}
          >
            iOS ViR
          </ActionLink>
        </>
      )}

      {hasValidPermissions && (
        <ActionLink
          href={collectionsUrl}
          title='Delete Collection'
          onClick={(ev) => {
            ev.preventDefault()
            handleDeleteCollection()
          }}
        >
          Delete
        </ActionLink>
      )}

      <CreateCollectionsModal
        ref={createCollectionsModalRef}
        onSuccess={handleCreateCollectionOnSuccess}
      />
    </ActionsLinks>
  )
}

export default AuthenticationConnect(ActionsConnector(Component))
